import { useEffect, useState } from 'react';
import { Loader2, Send, Users, Calendar, FileCode, X, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { formatScheduledForDisplay } from '../../lib/newsletterSchedule';

export type ConfirmSendMode = 'schedule' | 'send_now';

interface ConfirmSendModalProps {
  isOpen: boolean;
  mode: ConfirmSendMode;
  newsletterId: string;
  /** Scheduled send instant (ISO timestamptz). Used for the "schedule" path. */
  scheduledFor: string | null;
  /** Email subject; falls back to name when blank. */
  emailSubject: string | null;
  /** Newsletter name (reference title). */
  name: string | null;
  /** Channel slugs; empty array = all subscribers. */
  channels: string[];
  /** Selected Postmark template alias (or empty for raw HTML). */
  postmarkTemplateAlias: string | null;
  /** Pretty label for the selected template, if known. */
  templateLabel?: string | null;
  aiPersonalizationEnabled?: boolean;
  onClose: () => void;
  /** Called after successful materialize. The parent should refresh state. */
  onSuccess: (result: { inserted: number; recipients_total: number }) => void;
}

function prettifyChannelSlug(slug: string): string {
  return slug
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatScheduleDisplay(d: string | null): string {
  if (!d) return '—';
  return formatScheduledForDisplay(d);
}

export default function ConfirmSendModal({
  isOpen,
  mode,
  newsletterId,
  scheduledFor,
  emailSubject,
  name,
  channels,
  postmarkTemplateAlias,
  templateLabel,
  aiPersonalizationEnabled = false,
  onClose,
  onSuccess,
}: ConfirmSendModalProps) {
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [personalizationCounts, setPersonalizationCounts] = useState<{
    eligible: number;
    skipped: number;
    min_score: number;
  } | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  const [countError, setCountError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const effectiveDate = mode === 'send_now' ? null : scheduledFor;
  const subjectFallback = (emailSubject?.trim() || name?.trim() || 'Untitled').trim();
  const subjectIsFallback = !(emailSubject ?? '').trim();
  const alias = (postmarkTemplateAlias ?? '').trim();

  useEffect(() => {
    if (!isOpen) return;
    setSubmitError(null);
    setRecipientCount(null);
    setPersonalizationCounts(null);
    setCountError(null);
    setCountLoading(true);
    void (async () => {
      try {
        const { data, error } = await supabase.rpc('count_newsletter_recipients', {
          p_newsletter_id: newsletterId,
        });
        if (error) throw error;
        setRecipientCount(typeof data === 'number' ? data : 0);
        if (aiPersonalizationEnabled) {
          const { data: pData, error: pErr } = await supabase.rpc(
            'count_newsletter_personalization_recipients',
            { p_newsletter_id: newsletterId },
          );
          if (pErr) throw pErr;
          const obj = pData as {
            eligible?: number;
            skipped?: number;
            min_score?: number;
          } | null;
          if (obj && typeof obj === 'object') {
            setPersonalizationCounts({
              eligible: typeof obj.eligible === 'number' ? obj.eligible : 0,
              skipped: typeof obj.skipped === 'number' ? obj.skipped : 0,
              min_score: typeof obj.min_score === 'number' ? obj.min_score : 50,
            });
          }
        }
      } catch (e) {
        setCountError(e instanceof Error ? e.message : 'Failed to count recipients');
      } finally {
        setCountLoading(false);
      }
    })();
  }, [isOpen, newsletterId, aiPersonalizationEnabled]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, submitting, onClose]);

  if (!isOpen) return null;

  const confirmDisabled =
    submitting ||
    countLoading ||
    recipientCount == null ||
    recipientCount <= 0 ||
    (mode === 'schedule' && !scheduledFor);

  const handleConfirm = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body: Record<string, unknown> = { newsletter_id: newsletterId };
      if (mode === 'send_now') {
        body.override_scheduled_for = new Date().toISOString();
      }
      const { data, error } = await supabase.functions.invoke('materialize-newsletter', {
        body,
      });
      if (error) {
        const detail = (data as { error?: string } | null)?.error;
        throw new Error(detail || error.message);
      }
      const result = data as { inserted?: number; recipients_total?: number } | null;
      onSuccess({
        inserted: result?.inserted ?? 0,
        recipients_total: result?.recipients_total ?? recipientCount ?? 0,
      });
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Send failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={submitting ? undefined : onClose}
    >
      <div
        className="relative w-full max-w-lg rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Send className="w-4 h-4 text-emerald-500" />
            {mode === 'send_now' ? 'Send now' : 'Queue recipients'}
          </div>
          <button
            type="button"
            disabled={submitting}
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 text-sm text-gray-800 dark:text-gray-200">
          <div className="grid grid-cols-[20px_1fr] gap-x-3 gap-y-3">
            <Calendar className="w-4 h-4 text-gray-500 mt-0.5" />
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-0.5">
                Date &amp; time
              </div>
              {mode === 'send_now' ? (
                <div>
                  <div>Send immediately</div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400">
                    Queues recipients now and delivers on the next cron tick (within ~5 minutes).
                  </div>
                </div>
              ) : (
                <div>
                  <div>{formatScheduleDisplay(effectiveDate)}</div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400">
                    Queues one scheduled_emails row per subscriber now. Delivery at or shortly
                    after this time (cron checks every 5 minutes).
                    {aiPersonalizationEnabled ? (
                      <>
                        {' '}
                        AI personalization runs in the 24 hours before send, then delivery
                        proceeds.
                      </>
                    ) : null}
                  </div>
                </div>
              )}
            </div>

            <div className="w-4 h-4 mt-0.5 text-gray-500 text-center font-bold">@</div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-0.5">
                Subject
              </div>
              <div className="break-words">
                {subjectFallback}
                {subjectIsFallback ? (
                  <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 dark:text-amber-300">
                    <AlertTriangle className="w-3 h-3" />
                    using Name (no Email subject set)
                  </span>
                ) : null}
              </div>
            </div>

            <FileCode className="w-4 h-4 text-gray-500 mt-0.5" />
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-0.5">
                Template
              </div>
              <div>{alias ? templateLabel || alias : 'Raw HTML (no template)'}</div>
            </div>

            <Users className="w-4 h-4 text-gray-500 mt-0.5" />
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-0.5">
                Audience
              </div>
              {channels.length === 0 ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200">
                  All active subscribers
                </span>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {channels.map((s) => (
                    <span
                      key={s}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200"
                    >
                      {prettifyChannelSlug(s)}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <Users className="w-4 h-4 text-gray-500 mt-0.5" />
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-0.5">
                Recipients
              </div>
              {countLoading ? (
                <span className="inline-flex items-center gap-2 text-gray-500">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Counting…
                </span>
              ) : countError ? (
                <span className="text-red-600 dark:text-red-400">{countError}</span>
              ) : recipientCount != null ? (
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  {recipientCount.toLocaleString()}{' '}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                    subscriber{recipientCount === 1 ? '' : 's'}
                  </span>
                </span>
              ) : null}
            </div>

            {aiPersonalizationEnabled && personalizationCounts && recipientCount != null ? (
              <div className="grid grid-cols-[20px_1fr] gap-x-3">
                <Users className="w-4 h-4 text-violet-500 mt-0.5" />
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-0.5">
                    AI personalization
                  </div>
                  <div className="text-sm">
                    ~{personalizationCounts.eligible.toLocaleString()} of{' '}
                    {recipientCount.toLocaleString()} will be AI-tuned (score ≥
                    {personalizationCounts.min_score}).{' '}
                    {personalizationCounts.skipped > 0 ? (
                      <span className="text-gray-500 dark:text-gray-400">
                        {personalizationCounts.skipped.toLocaleString()} will get the
                        base newsletter (low profile).
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {mode === 'schedule' && !scheduledFor ? (
            <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
              No planned send time set. Open Settings and pick a date and time before queuing
              recipients.
            </div>
          ) : null}

          {recipientCount === 0 && !countLoading && !countError ? (
            <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
              No active subscribers match the current audience. Adjust the
              Audience filter in Edit details, then try again.
            </div>
          ) : null}

          {submitError ? (
            <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-800 dark:text-red-200">
              {submitError}
            </div>
          ) : null}
        </div>

        <div className="shrink-0 px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={confirmDisabled}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {submitting
              ? 'Queuing…'
              : mode === 'send_now'
                ? `Queue & send to ${recipientCount ?? '…'}`
                : `Queue ${recipientCount ?? '…'} recipients`}
          </button>
        </div>
      </div>
    </div>
  );
}
